import { useMemo } from "react";
import { useAppSelector } from "app/store";
import { getGaiaObjectCategory, parseType } from "utils/gaiaobject-utils";
import { useGetUIContentQuery } from "./endpoints";
import { applyUiContent, initialState } from "./slice";
import { IUiContentState } from "./definitions";

export const useGaiaObjectUiContent = (rawType?: string) => {
  const type = parseType(rawType);

  // Simply select from already-loaded data (loaded by Header component)
  const litigationUiContent = useAppSelector(
    (state) => state.litigationUiContent,
  );
  const noticeUiContent = useAppSelector((state) => state.noticeUiContent);
  const defaultUiContent = useAppSelector((state) => state.uiContent);

  if (type === "litigation") return litigationUiContent;
  if (type === "notice") return noticeUiContent;
  return defaultUiContent;
};

// Accepts getGaiaObjectCategory's plural keys and getCategoryById's singular keys. An id
// of no known category yields no page, so the query is skipped rather than falling back
// to some other team's audit content.
const PAGE_BY_CATEGORY: Record<string, string | undefined> = {
  audit: "createaudit",
  notice: "notice",
  notices: "notice",
  litigation: "litigation",
  litigations: "litigation",
};

// Member and guest lists describe the viewer, not the resource, so they stay viewer-scoped.
const VIEWER_SCOPED_FIELDS = [
  "ownerOptions",
  "criticalOwnerOptions",
  "teamAndAllGuestOwnerOptions",
  "teamAndCurResourceGuestOwnerOptions",
] as const;

// Every option list empty, so a team whose content is not loaded never shows placeholders
// or another team's options.
const EMPTY_UI_CONTENT = Object.fromEntries(
  Object.entries(initialState).map(([key, value]) => [
    key,
    Array.isArray(value) ? [] : value,
  ]),
) as unknown as IUiContentState;

/**
 * UI content of the given team for a category. Without a team, or for a category that has
 * no page, this is the viewer's content. With a team, the content is that team's once
 * loaded and empty until then or if the request fails.
 */
export const useOwnerUiContent = (
  category?: string,
  uiContentTeamName?: string | null,
) => {
  const page = PAGE_BY_CATEGORY[category || ""] ?? "";
  const teamName = uiContentTeamName || "";
  const scoped = !!teamName && !!page;

  // currentData is only set for the current key; data would keep the previous team's.
  const { currentData } = useGetUIContentQuery(
    { teamName, page },
    { skip: !scoped },
  );

  const viewerUiContent = useGaiaObjectUiContent(category);

  return useMemo(() => {
    if (!scoped) {
      return viewerUiContent;
    }
    const content: IUiContentState = currentData
      ? { ...initialState }
      : { ...EMPTY_UI_CONTENT };
    if (currentData) {
      applyUiContent(content, currentData.content);
    }
    VIEWER_SCOPED_FIELDS.forEach((field) => {
      content[field] = viewerUiContent[field];
    });
    return content;
  }, [scoped, currentData, viewerUiContent]);
};

/** UI content for a task, taken from the team that owns its parent. */
export const useTaskUiContent = (
  gaiaObjectId?: string,
  uiContentTeamName?: string | null,
) =>
  useOwnerUiContent(
    getGaiaObjectCategory(gaiaObjectId || ""),
    uiContentTeamName,
  );
